import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid2 as Grid, Box, Typography, Accordion, AccordionSummary, AccordionDetails, Divider, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useFinanceDetails, useFinanceSave } from '../../hooks/queries/useFinance';
import { useFinanceDefaultsPrompt } from '../../hooks/queries/useFinanceDefaultsPrompt';
import { useSnackbar } from '../../hooks/useSnackbar';
import FinanceItemsTable from './FinanceItemsTable';
import FinanceAddItemDialog from './FinanceAddItemDialog';
import FinancePromptDefaultsDialog from './FinancePromptDefaultsDialog';
import PhoneDisplay from '../ui/PhoneDisplay';
import { getPaymentTypeIcon } from '../../utils/paymentTypeIcons';

function CompactField({ label, value }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.25, minHeight: 22 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 82, flexShrink: 0, textTransform: 'uppercase', letterSpacing: 0.3, fontSize: '0.68rem' }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

export default function FinanceDetailsDialog({ open, appointmentId, onClose }) {
  const { data: details, isLoading } = useFinanceDetails(appointmentId);
  const saveMutation = useFinanceSave();
  const { shouldAsk, suppressForToday } = useFinanceDefaultsPrompt();
  const { showSuccess, showError } = useSnackbar();

  const [gross, setGross] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [items, setItems] = useState([]);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [editItemIndex, setEditItemIndex] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [promptState, setPromptState] = useState(null);

  useEffect(() => {
    if (details) {
      setGross(details.grossValue);
      setPaymentId(details.paymentId);
      setItems(details.financialItems || []);
    }
  }, [details, appointmentId, open]);

  const handleSave = async () => {
    try {
      const payload = {
        grossValue: gross,
        paymentId: paymentId || null,
        items,
        promoteGrossValueAsDefault: false,
        linkPaymentToSchedule: false,
      };
      const result = await saveMutation.mutateAsync({ id: appointmentId, payload });
      showSuccess('Valores salvos');

      // Pós-save: perguntas condicionais
      const askPrice = !result.serviceHasDefaultPrice
        && gross != null
        && shouldAsk('defaultPrice');

      const askPayment = paymentId
        && !result.paymentLinkedToCombo
        && shouldAsk('paymentLink');

      if (askPrice || askPayment) {
        setPromptState({ askPrice, askPayment, appointmentId });
      } else {
        onClose();
      }
    } catch (err) {
      showError(err.message || 'Erro ao salvar');
    }
  };

  const handleSaveItem = (newItem) => {
    if (editItemIndex !== null) {
      setItems(items.map((it, i) => (i === editItemIndex ? newItem : it)));
    } else {
      setItems([...items, newItem]);
    }
    setAddItemOpen(false);
    setEditItemIndex(null);
    setEditItem(null);
  };

  const handleEditItem = (index, item) => {
    setEditItemIndex(index);
    setEditItem(item);
    setAddItemOpen(true);
  };

  const handleDeleteItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          Detalhes Financeiros — {details?.clientName || 'Carregando...'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <CompactField label="Cliente" value={details?.clientName} />
              <CompactField label="Telefone" value={details?.clientPhone ? <PhoneDisplay value={details.clientPhone} /> : null} />
              <CompactField label="Email" value={details?.clientEmail} />
              <CompactField
                label="Data"
                value={details?.startTime
                  ? new Intl.DateTimeFormat('pt-BR', {
                      timeZone: 'America/Sao_Paulo',
                      dateStyle: 'long',
                      timeStyle: 'short',
                    }).format(new Date(details.startTime))
                  : null}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <CompactField label="Serviço" value={details?.serviceName} />
              <CompactField label="Profissional" value={details?.professionalName} />
              <CompactField label="Local" value={details?.placeName} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ minWidth: 82, textTransform: 'uppercase', letterSpacing: 0.3, fontSize: '0.68rem' }}
                >
                  Pagamento
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {details?.paymentOptions?.map((p) => {
                    const selected = paymentId === p.id;
                    return (
                      <Tooltip key={p.id} title={p.name} arrow>
                        <Box
                          onClick={() => setPaymentId(selected ? null : p.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setPaymentId(selected ? null : p.id);
                            }
                          }}
                          sx={{
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                            border: '1.5px solid',
                            borderColor: selected ? 'primary.main' : 'divider',
                            bgcolor: selected ? 'primary.50' : 'background.paper',
                            color: selected ? 'primary.main' : 'text.secondary',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            maxWidth: 150,
                            '&:hover': { borderColor: 'primary.light', color: 'primary.main' },
                          }}
                        >
                          {getPaymentTypeIcon(p.type, { sx: { fontSize: 16 } })}
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{ fontWeight: selected ? 600 : 400, fontSize: '0.72rem' }}
                          >
                            {p.name}
                          </Typography>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            </Grid>
          </Grid>

          {details?.history && details.history.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Accordion defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">Histórico do Agendamento</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {details.history.map((event, idx) => (
                      <Box key={idx} sx={{ pb: 1, borderBottom: idx < details.history.length - 1 ? '1px solid #eee' : 'none' }}>
                        <Typography variant="body2" color="text.secondary">
                          {new Intl.DateTimeFormat('pt-BR', {
                            timeZone: 'America/Sao_Paulo',
                            dateStyle: 'short',
                            timeStyle: 'short'
                          }).format(new Date(event.timestamp || event.date))}
                        </Typography>
                        <Typography variant="body2">{event.description || event.action || event.status}</Typography>
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </>
          )}


          <FinanceItemsTable
            grossValue={gross}
            onGrossChange={setGross}
            items={items}
            onItemsChange={setItems}
            onDeleteItem={handleDeleteItem}
            onEditItem={handleEditItem}
            onAddClick={() => { setEditItemIndex(null); setEditItem(null); setAddItemOpen(true); }}
            serviceName={details?.serviceName}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saveMutation.isPending}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <FinanceAddItemDialog
        open={addItemOpen}
        onClose={() => { setAddItemOpen(false); setEditItemIndex(null); setEditItem(null); }}
        onSubmit={handleSaveItem}
        initialItem={editItem}
        itemsCount={items.length}
      />

      {promptState && (
        <FinancePromptDefaultsDialog
          open={!!promptState}
          askPrice={promptState.askPrice}
          askPayment={promptState.askPayment}
          grossValue={gross}
          paymentId={paymentId}
          paymentName={details?.paymentOptions?.find(p => p.id === paymentId)?.name}
          onFinish={() => {
            setPromptState(null);
            onClose();
          }}
          suppressForToday={suppressForToday}
          saveMutation={saveMutation}
          appointmentId={appointmentId}
          items={items}
        />
      )}
    </>
  );
}
